import { CredentialClaimValues } from '../../api';
import { ExtendedTruvityClient } from '../../ExtendedClient';
import { LinkedCredential, LinkedFile, Rfc3339Date, Rfc3339DateTime } from '../models';
import { UserUpdateClaims } from '../types';

import { UdtClaims } from './claims';
import { Baz, BazVcContext, Qux, QuxVcContext } from './testdata/udt';
import { Json } from './types';

const createDummyApiClient = () => ({}) as ExtendedTruvityClient;

describe('UdtClaims', () => {
    describe('using VcContext', () => {
        it('should enhance linkable fields with corresponding class instances', async () => {
            const claims = new UdtClaims(createDummyApiClient(), BazVcContext);
            const apiReceivedClaims: Json = {
                field1: {
                    id: 'urn:credential:111',
                    type: 'LinkedCredential',
                    digestMultibase: 'digestMultibase',
                },
                field2: {
                    id: 'urn:file:222',
                    type: 'LinkedFile',
                    digestMultibase: 'digestMultibase',
                },
                field3: {
                    bar: '333',
                },
                field4: 'option2',
                field5: [{ id: 'urn:credential:111', type: 'LinkedCredential' }],
                field6: {
                    linkedProp: {
                        id: 'urn:credential:111',
                        type: 'LinkedCredential',
                        digestMultibase: 'digestMultibase',
                    },
                },
                field7: new Rfc3339DateTime().toJSON(),
                field8: new Rfc3339Date().toJSON(),
                field11_1: ['field11_1_value0'],
                field11_2: ['field11_2_value0'],
                field11_3: ['field11_3_value0'],
                field12_1: [{ bar: 'field12_1_value0' }],
                field12_2: [{ bar: 'field12_2_value0' }],
                field12_3: [{ bar: 'field12_3_value0' }],
            };
            const enhancedClaims = claims.unmarshalValuesToCredential(apiReceivedClaims);

            expect(enhancedClaims.field1).toBeInstanceOf(LinkedCredential);
            expect(enhancedClaims.field1?.id).toBe('urn:credential:111');
            expect(enhancedClaims.field1?.type).toBe('LinkedCredential');
            expect(enhancedClaims.field1?.digestMultibase).toBe('digestMultibase');

            expect(enhancedClaims.field2).toBeInstanceOf(LinkedFile);
            expect(enhancedClaims.field2.id).toBe('urn:file:222');
            expect(enhancedClaims.field2.type).toBe('LinkedFile');
            expect(enhancedClaims.field2.digestMultibase).toBe('digestMultibase');

            // Non-linkable fields should be resolved as-is
            expect(enhancedClaims.field3).toEqual({
                bar: '333',
            });

            expect(enhancedClaims.field4).toEqual('option2');

            expect(enhancedClaims.field5[0]).toBeInstanceOf(LinkedCredential);

            expect(enhancedClaims.field6.linkedProp).toBeInstanceOf(LinkedCredential);
            expect(enhancedClaims.field6.linkedProp?.id).toBe('urn:credential:111');
            expect(enhancedClaims.field6.linkedProp?.type).toBe('LinkedCredential');
            expect(enhancedClaims.field6.linkedProp?.digestMultibase).toBe('digestMultibase');

            expect(enhancedClaims.field7).toBeInstanceOf(Rfc3339DateTime);
            expect(enhancedClaims.field8).toBeInstanceOf(Rfc3339Date);

            expect(enhancedClaims.field11_1).toEqual(['field11_1_value0']);
            expect(enhancedClaims.field11_2).toEqual(['field11_2_value0']);
            expect(enhancedClaims.field11_3).toEqual(['field11_3_value0']);

            expect(enhancedClaims.field12_1).toEqual([{ bar: 'field12_1_value0' }]);
            expect(enhancedClaims.field12_2).toEqual([{ bar: 'field12_2_value0' }]);
            expect(enhancedClaims.field12_3).toEqual([{ bar: 'field12_3_value0' }]);
        });

        it('should map CredentialClaimValues to corresponding class instances', async () => {
            const claims = new UdtClaims(createDummyApiClient(), BazVcContext);
            const credentialClaimValues: CredentialClaimValues = {
                field1: {
                    kind: 'LINK',
                    value: {
                        id: 'urn:credential:111',
                        digestMultibase: 'digestMultibase',
                    },
                },
                field2: {
                    kind: 'FILE',
                    value: {
                        id: 'urn:file:222',
                        digestMultibase: 'digestMultibase',
                    },
                },
                field3: {
                    kind: 'STRUCT',
                    value: {
                        bar: { kind: 'STRING', value: '333' },
                    },
                },
                field4: {
                    kind: 'STRING',
                    value: 'option2',
                },
                field5: {
                    kind: 'ARRAY',
                    value: [{ kind: 'LINK', value: { id: 'urn:credential:111' } }],
                },
                field6: {
                    kind: 'STRUCT',
                    value: {
                        linkedProp: {
                            kind: 'LINK',
                            value: {
                                id: 'urn:credential:111',
                                digestMultibase: 'digestMultibase',
                            },
                        },
                    },
                },
                field7: {
                    kind: 'DATETIME',
                    value: new Rfc3339DateTime().toDate(),
                },
                field8: {
                    kind: 'DATE',
                    value: new Rfc3339Date().toString(),
                },
                field11_1: { kind: 'ARRAY', value: [{ kind: 'STRING', value: 'field11_1_value0' }] },
                field11_2: { kind: 'ARRAY', value: [{ kind: 'STRING', value: 'field11_2_value0' }] },
                field11_3: { kind: 'ARRAY', value: [{ kind: 'STRING', value: 'field11_3_value0' }] },
                field12_1: {
                    kind: 'ARRAY',
                    value: [{ kind: 'STRUCT', value: { bar: { kind: 'STRING', value: 'field12_1_value0' } } }],
                },
                field12_2: {
                    kind: 'ARRAY',
                    value: [{ kind: 'STRUCT', value: { bar: { kind: 'STRING', value: 'field12_2_value0' } } }],
                },
                field12_3: {
                    kind: 'ARRAY',
                    value: [{ kind: 'STRUCT', value: { bar: { kind: 'STRING', value: 'field12_3_value0' } } }],
                },
            };
            const enhancedClaims = claims.unmarshalValuesToDraft(credentialClaimValues);

            expect(enhancedClaims.field1).toBeInstanceOf(LinkedCredential);
            expect(enhancedClaims.field1?.id).toBe('urn:credential:111');
            expect(enhancedClaims.field1?.type).toBe('LinkedCredential');
            expect(enhancedClaims.field1?.digestMultibase).toBe('digestMultibase');

            expect(enhancedClaims.field2).toBeInstanceOf(LinkedFile);
            expect(enhancedClaims.field2?.id).toBe('urn:file:222');
            expect(enhancedClaims.field2?.type).toBe('LinkedFile');
            expect(enhancedClaims.field2?.digestMultibase).toBe('digestMultibase');

            // Non-linkable fields should be resolved as-is
            expect(enhancedClaims.field3).toEqual({
                bar: '333',
            });

            expect(enhancedClaims.field4).toEqual('option2');

            expect(enhancedClaims.field5 ? enhancedClaims.field5[0] : undefined).toBeInstanceOf(LinkedCredential);

            expect(enhancedClaims.field6?.linkedProp).toBeInstanceOf(LinkedCredential);
            expect(enhancedClaims.field6?.linkedProp?.id).toBe('urn:credential:111');
            expect(enhancedClaims.field6?.linkedProp?.type).toBe('LinkedCredential');
            expect(enhancedClaims.field6?.linkedProp?.digestMultibase).toBe('digestMultibase');

            expect(enhancedClaims.field7).toBeInstanceOf(Rfc3339DateTime);
            expect(enhancedClaims.field8).toBeInstanceOf(Rfc3339Date);

            expect(enhancedClaims.field11_1).toEqual(['field11_1_value0']);
            expect(enhancedClaims.field11_2).toEqual(['field11_2_value0']);
            expect(enhancedClaims.field11_3).toEqual(['field11_3_value0']);

            expect(enhancedClaims.field12_1).toEqual([{ bar: 'field12_1_value0' }]);
            expect(enhancedClaims.field12_2).toEqual([{ bar: 'field12_2_value0' }]);
            expect(enhancedClaims.field12_3).toEqual([{ bar: 'field12_3_value0' }]);
        });

        it('should properly serialize linked entities', async () => {
            const claims = new UdtClaims(createDummyApiClient(), BazVcContext);
            const userSentClaims: UserUpdateClaims<BazVcContext> = {
                field1: {
                    id: '111',
                    digestMultibase: 'digestMultibase',
                },
                field2: {
                    id: '222',
                },
                field5: [
                    {
                        id: '111',
                    },
                ],
                field6: {
                    linkedProp: {
                        id: '111',
                    },
                },
                field7: new Rfc3339DateTime(),
                field8: new Rfc3339Date(),
                field11_1: ['field11_1_value0'],
                field11_2: ['field11_2_value0'],
                field11_3: ['field11_3_value0'],
                field12_1: [{ bar: 'field12_1_value0' }],
                field12_2: [{ bar: 'field12_2_value0' }],
                field12_3: [{ bar: 'field12_3_value0' }],
            };
            const enhancedClaims = claims.marshalValues(userSentClaims);

            expect(enhancedClaims.field1.kind).toBe('LINK');
            expect(enhancedClaims.field1.value.id).toBe('111');
            expect(enhancedClaims.field1.value.digestMultibase).toBe('digestMultibase');

            expect(enhancedClaims.field2.kind).toBe('FILE');
            expect(enhancedClaims.field2.value.id).toBe('222');

            expect(enhancedClaims.field5.kind).toBe('ARRAY');
            expect(enhancedClaims.field5.value.length).toBe(1);
            expect(enhancedClaims.field5.value[0].kind).toBe('LINK');
            expect(enhancedClaims.field5.value[0].value.id).toBe('111');

            expect(enhancedClaims.field6.kind).toBe('STRUCT');
            expect(enhancedClaims.field6.value.linkedProp?.kind).toBe('LINK');
            expect(enhancedClaims.field6.value.linkedProp?.value?.id).toBe('111');

            expect(enhancedClaims.field7.kind).toBe('DATETIME');
            expect(enhancedClaims.field7.value).toBeInstanceOf(Date);
            expect(enhancedClaims.field8.kind).toBe('DATE');
            expect(typeof enhancedClaims.field8.value).toBe('string');

            expect(enhancedClaims.field11_1).toEqual({
                kind: 'ARRAY',
                value: [{ kind: 'STRING', value: 'field11_1_value0' }],
            });
            expect(enhancedClaims.field11_2).toEqual({
                kind: 'ARRAY',
                value: [{ kind: 'STRING', value: 'field11_2_value0' }],
            });
            expect(enhancedClaims.field11_3).toEqual({
                kind: 'ARRAY',
                value: [{ kind: 'STRING', value: 'field11_3_value0' }],
            });

            expect(enhancedClaims.field12_1).toEqual({
                kind: 'ARRAY',
                value: [{ kind: 'STRUCT', value: { bar: { kind: 'STRING', value: 'field12_1_value0' } } }],
            });
            expect(enhancedClaims.field12_2).toEqual({
                kind: 'ARRAY',
                value: [{ kind: 'STRUCT', value: { bar: { kind: 'STRING', value: 'field12_2_value0' } } }],
            });
            expect(enhancedClaims.field12_3).toEqual({
                kind: 'ARRAY',
                value: [{ kind: 'STRUCT', value: { bar: { kind: 'STRING', value: 'field12_3_value0' } } }],
            });
        });

        it('should map registered claims to api', async () => {
            const claims = new UdtClaims(createDummyApiClient(), QuxVcContext);
            const schema = claims.getClaimsSchema();

            expect(schema).toEqual({
                fields: [
                    { name: 'field1_1', kind: 'LINK', notEmpty: true },
                    { name: 'field1_2', kind: 'LINK', notEmpty: true },
                    { name: 'field1_3', kind: 'LINK' },
                    { name: 'field1_4', kind: 'LINK' },

                    { name: 'field2_1', notEmpty: true, kind: 'FILE' },
                    { name: 'field2_2', notEmpty: true, kind: 'FILE' },

                    {
                        name: 'field3_1',
                        notEmpty: true,
                        kind: 'STRING',
                        enum: [
                            { value: 'option1', title: 'Option 1' },
                            { value: 'option2', title: 'Option 2' },
                        ],
                    },
                    {
                        name: 'field3_2',
                        notEmpty: true,
                        kind: 'STRING',
                        enum: [
                            { value: 'option1', title: 'Option 1' },
                            { value: 'option2', title: 'Option 2' },
                        ],
                    },

                    { name: 'field4_1', kind: 'NUMBER', notEmpty: true, isArray: true },
                    { name: 'field4_2', kind: 'NUMBER', notEmpty: true, isArray: true },

                    { name: 'field5_1', kind: 'NUMBER', notEmpty: true, title: 'Field title' },
                    { name: 'field5_2', kind: 'NUMBER', notEmpty: true, title: 'Field title' },

                    { name: 'field6_1', kind: 'NUMBER', notEmpty: true },
                    { name: 'field6_2', kind: 'NUMBER', notEmpty: true },

                    { name: 'field7_1', kind: 'DATETIME', notEmpty: true, isArray: true },
                    { name: 'field7_2', kind: 'DATETIME', notEmpty: true, isArray: true },

                    { name: 'field8_1', kind: 'DATE', notEmpty: true, isArray: true },
                    { name: 'field8_2', kind: 'DATE', notEmpty: true, isArray: true },

                    {
                        name: 'field9',
                        notEmpty: true,
                        kind: 'STRUCT',
                        fields: [{ name: 'bar', notEmpty: true, kind: 'STRING' }],
                        jsonLdBaseIri: 'https://www.w3.org/ns/credentials/issuer-dependent',
                        jsonLdTermName: 'Bar',
                    },

                    {
                        name: 'field10',
                        title: 'Field 10',
                        isArray: true,
                        kind: 'STRUCT',
                        fields: [{ name: 'bar', notEmpty: true, kind: 'STRING' }],
                        jsonLdBaseIri: 'https://www.w3.org/ns/credentials/issuer-dependent',
                        jsonLdTermName: 'Bar',
                    },

                    { name: 'field11_1', kind: 'STRING', isArray: true },
                    { name: 'field11_2', kind: 'STRING', isArray: true },
                    { name: 'field11_3', kind: 'STRING', isArray: true },
                ],
                jsonLdBaseIri: 'https://www.w3.org/ns/credentials/issuer-dependent',
                jsonLdTermName: 'Qux',
            });
        });
    });

    describe('using VcSchema', () => {
        it('should enhance linkable fields with corresponding class instances', async () => {
            const claims = new UdtClaims(createDummyApiClient(), Baz);
            const apiReceivedClaims: Json = {
                field1: {
                    id: 'urn:credential:111',
                    type: 'LinkedCredential',
                    digestMultibase: 'digestMultibase',
                },
                field2: {
                    id: 'urn:file:222',
                    type: 'LinkedFile',
                    digestMultibase: 'digestMultibase',
                },
                field3: {
                    bar: '333',
                },
                field4: 'option2',
                field5: [{ id: 'urn:credential:111', type: 'LinkedCredential' }],
                field6: {
                    linkedProp: {
                        id: 'urn:credential:111',
                        type: 'LinkedCredential',
                        digestMultibase: 'digestMultibase',
                    },
                },
                field7: new Rfc3339DateTime().toJSON(),
                field8: new Rfc3339Date().toJSON(),
                field11_1: ['field11_1_value0'],
                field11_2: ['field11_2_value0'],
                field11_3: ['field11_3_value0'],
                field12_1: [{ bar: 'field12_1_value0' }],
                field12_2: [{ bar: 'field12_2_value0' }],
                field12_3: [{ bar: 'field12_3_value0' }],
            };
            const enhancedClaims = claims.unmarshalValuesToCredential(apiReceivedClaims);

            expect(enhancedClaims.field1).toBeInstanceOf(LinkedCredential);
            expect(enhancedClaims.field1?.id).toBe('urn:credential:111');
            expect(enhancedClaims.field1?.type).toBe('LinkedCredential');
            expect(enhancedClaims.field1?.digestMultibase).toBe('digestMultibase');

            expect(enhancedClaims.field2).toBeInstanceOf(LinkedFile);
            expect(enhancedClaims.field2.id).toBe('urn:file:222');
            expect(enhancedClaims.field2.type).toBe('LinkedFile');
            expect(enhancedClaims.field2.digestMultibase).toBe('digestMultibase');

            // Non-linkable fields should be resolved as-is
            expect(enhancedClaims.field3).toEqual({
                bar: '333',
            });

            expect(enhancedClaims.field4).toEqual('option2');

            expect(enhancedClaims.field5[0]).toBeInstanceOf(LinkedCredential);

            expect(enhancedClaims.field6.linkedProp).toBeInstanceOf(LinkedCredential);
            expect(enhancedClaims.field6.linkedProp?.id).toBe('urn:credential:111');
            expect(enhancedClaims.field6.linkedProp?.type).toBe('LinkedCredential');
            expect(enhancedClaims.field6.linkedProp?.digestMultibase).toBe('digestMultibase');

            expect(enhancedClaims.field7).toBeInstanceOf(Rfc3339DateTime);
            expect(enhancedClaims.field8).toBeInstanceOf(Rfc3339Date);

            expect(enhancedClaims.field11_1).toEqual(['field11_1_value0']);
            expect(enhancedClaims.field11_2).toEqual(['field11_2_value0']);
            expect(enhancedClaims.field11_3).toEqual(['field11_3_value0']);

            expect(enhancedClaims.field12_1).toEqual([{ bar: 'field12_1_value0' }]);
            expect(enhancedClaims.field12_2).toEqual([{ bar: 'field12_2_value0' }]);
            expect(enhancedClaims.field12_3).toEqual([{ bar: 'field12_3_value0' }]);
        });

        it('should properly serialize linked entities', async () => {
            const claims = new UdtClaims(createDummyApiClient(), Baz);
            const userSentClaims: UserUpdateClaims<Baz> = {
                field1: {
                    id: '111',
                    digestMultibase: 'digestMultibase',
                },
                field2: {
                    id: '222',
                },
                field5: [
                    {
                        id: '111',
                    },
                ],
                field6: {
                    linkedProp: {
                        id: '111',
                    },
                },
                field7: new Rfc3339DateTime(),
                field8: new Rfc3339Date(),
                field11_1: ['field11_1_value0'],
                field11_2: ['field11_2_value0'],
                field11_3: ['field11_3_value0'],
                field12_1: [{ bar: 'field12_1_value0' }],
                field12_2: [{ bar: 'field12_2_value0' }],
                field12_3: [{ bar: 'field12_3_value0' }],
            };
            const enhancedClaims = claims.marshalValues(userSentClaims);

            expect(enhancedClaims.field1.kind).toBe('LINK');
            expect(enhancedClaims.field1.value.id).toBe('111');
            expect(enhancedClaims.field1.value.digestMultibase).toBe('digestMultibase');

            expect(enhancedClaims.field2.kind).toBe('FILE');
            expect(enhancedClaims.field2.value.id).toBe('222');

            expect(enhancedClaims.field5.kind).toBe('ARRAY');
            expect(enhancedClaims.field5.value.length).toBe(1);
            expect(enhancedClaims.field5.value[0].kind).toBe('LINK');
            expect(enhancedClaims.field5.value[0].value.id).toBe('111');

            expect(enhancedClaims.field6.kind).toBe('STRUCT');
            expect(enhancedClaims.field6.value.linkedProp?.kind).toBe('LINK');
            expect(enhancedClaims.field6.value.linkedProp?.value?.id).toBe('111');

            expect(enhancedClaims.field7.kind).toBe('DATETIME');
            expect(enhancedClaims.field7.value).toBeInstanceOf(Date);
            expect(enhancedClaims.field8.kind).toBe('DATE');
            expect(typeof enhancedClaims.field8.value).toBe('string');

            expect(enhancedClaims.field11_1).toEqual({
                kind: 'ARRAY',
                value: [{ kind: 'STRING', value: 'field11_1_value0' }],
            });
            expect(enhancedClaims.field11_2).toEqual({
                kind: 'ARRAY',
                value: [{ kind: 'STRING', value: 'field11_2_value0' }],
            });
            expect(enhancedClaims.field11_3).toEqual({
                kind: 'ARRAY',
                value: [{ kind: 'STRING', value: 'field11_3_value0' }],
            });

            expect(enhancedClaims.field12_1).toEqual({
                kind: 'ARRAY',
                value: [{ kind: 'STRUCT', value: { bar: { kind: 'STRING', value: 'field12_1_value0' } } }],
            });
            expect(enhancedClaims.field12_2).toEqual({
                kind: 'ARRAY',
                value: [{ kind: 'STRUCT', value: { bar: { kind: 'STRING', value: 'field12_2_value0' } } }],
            });
            expect(enhancedClaims.field12_3).toEqual({
                kind: 'ARRAY',
                value: [{ kind: 'STRUCT', value: { bar: { kind: 'STRING', value: 'field12_3_value0' } } }],
            });
        });

        it('should map registered claims to api', async () => {
            const claims = new UdtClaims(createDummyApiClient(), Qux);
            const schema = claims.getClaimsSchema();

            expect(schema).toEqual({
                fields: [
                    { name: 'field1_1', kind: 'LINK', notEmpty: true },
                    { name: 'field1_2', kind: 'LINK', notEmpty: true },
                    { name: 'field1_3', kind: 'LINK' },
                    { name: 'field1_4', kind: 'LINK' },

                    { name: 'field2_1', notEmpty: true, kind: 'FILE' },
                    { name: 'field2_2', notEmpty: true, kind: 'FILE' },

                    {
                        name: 'field3_1',
                        notEmpty: true,
                        kind: 'STRING',
                        enum: [
                            { value: 'option1', title: 'Option 1' },
                            { value: 'option2', title: 'Option 2' },
                        ],
                    },
                    {
                        name: 'field3_2',
                        notEmpty: true,
                        kind: 'STRING',
                        enum: [
                            { value: 'option1', title: 'Option 1' },
                            { value: 'option2', title: 'Option 2' },
                        ],
                    },

                    { name: 'field4_1', kind: 'NUMBER', notEmpty: true, isArray: true },
                    { name: 'field4_2', kind: 'NUMBER', notEmpty: true, isArray: true },

                    { name: 'field5_1', kind: 'NUMBER', notEmpty: true, title: 'Field title' },
                    { name: 'field5_2', kind: 'NUMBER', notEmpty: true, title: 'Field title' },

                    { name: 'field6_1', kind: 'NUMBER', notEmpty: true },
                    { name: 'field6_2', kind: 'NUMBER', notEmpty: true },

                    { name: 'field7_1', kind: 'DATETIME', notEmpty: true, isArray: true },
                    { name: 'field7_2', kind: 'DATETIME', notEmpty: true, isArray: true },

                    { name: 'field8_1', kind: 'DATE', notEmpty: true, isArray: true },
                    { name: 'field8_2', kind: 'DATE', notEmpty: true, isArray: true },

                    {
                        name: 'field9',
                        notEmpty: true,
                        kind: 'STRUCT',
                        fields: [{ name: 'bar', notEmpty: true, kind: 'STRING' }],
                    },

                    {
                        name: 'field10',
                        title: 'Field 10',
                        isArray: true,
                        kind: 'STRUCT',
                        fields: [{ name: 'bar', notEmpty: true, kind: 'STRING' }],
                    },

                    { name: 'field11_1', kind: 'STRING', isArray: true },
                    { name: 'field11_2', kind: 'STRING', isArray: true },
                    { name: 'field11_3', kind: 'STRING', isArray: true },
                ],
            });
        });
    });
});
